import type { GameData } from "@/app/games/(details)/types";
import { Action } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { ImageBackground } from "../ui/custom/image-background";

const HAS_PLAYED_INTROS_KEY = "hasPlayedIntros";

type HasPlayedIntros = Record<string, boolean>;

interface VideoIntroProps {
  gameData: GameData;
  children: React.ReactNode;
}

const VideoIntroEnabled = ({ gameData, children }: VideoIntroProps) => {
  const [showContent, setShowContent] = useState(false);
  const initialViewedRef = useRef(false);

  const { track } = useGamePageAnalytics();

  useEffect(() => {
    // add overflow hidden to the body
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (initialViewedRef.current) return;
    initialViewedRef.current = true;

    track({
      action: Action.VIEWED,
      extras: {
        control: "start",
        section: GamePageSection.VIDEO_INTRO,
      },
    });
  }, [track]);

  const handleVideoEnd = useCallback(() => {
    console.log("handleVideoEnd");
    if (gameData.details.slug) {
      try {
        const existingIntros: HasPlayedIntros = JSON.parse(
          localStorage.getItem(HAS_PLAYED_INTROS_KEY) || "{}",
        );
        localStorage.setItem(
          HAS_PLAYED_INTROS_KEY,
          JSON.stringify({ ...existingIntros, [gameData.details.slug]: true }),
        );
      } catch (error) {
        localStorage.setItem(
          HAS_PLAYED_INTROS_KEY,
          JSON.stringify({ [gameData.details.slug]: true }),
        );
      }
    }

    track({
      action: Action.VIEWED,
      extras: {
        control: "end",
        section: GamePageSection.VIDEO_INTRO,
      },
    });

    // First fade in the overlay
    setShowContent(true);

    setTimeout(() => {
      document.body.style.overflow = "";
    }, 500);
  }, [track, gameData.details.slug]);

  return (
    <>
      {/* Video - hide once ended */}
      {gameData.gameContent?.video_splash_url ? (
        <video
          src={gameData.gameContent?.video_splash_url}
          muted
          autoPlay
          onEnded={handleVideoEnd}
          className={cn(
            "fixed inset-0 w-screen min-h-lvh object-cover [--media-object-fit:cover] transition-opacity duration-500",
            showContent ? "z-0 opacity-0" : "z-10 opacity-100",
          )}
          playsInline
        />
      ) : null}

      {/* Use background image instead of video as bg when video is ended, animate opacity of image */}
      {gameData.details.background_url ? (
        <ImageBackground
          staticImageUrl={gameData.details.background_url}
          backdropClassName="bg-black/50"
          className={cn(
            "transition-opacity duration-700",
            showContent ? "opacity-100" : "opacity-0",
          )}
        />
      ) : null}

      {/* Page Content */}
      <div
        className={cn(
          "flex w-full h-full transition-all duration-500",
          showContent
            ? // only animate translate-y on views without the bottom floating CTA, this breaks fixed positioning
              "opacity-100"
            : "opacity-0",
        )}
      >
        {children}
      </div>
    </>
  );
};

export const VideoIntro = ({ children, gameData }: VideoIntroProps) => {
  // Use LaunchDarkly flag to control video display
  // Configure in LaunchDarkly: device.deviceType = "desktop" → true, "non-desktop" → false
  const { showIntroVideo } = useFeatureFlags();

  const [hasPlayedIntro, setHasPlayedIntro] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!gameData.details.slug) return;

    try {
      setHasPlayedIntro(
        JSON.parse(localStorage.getItem(HAS_PLAYED_INTROS_KEY) || "{}")[
          gameData.details.slug
        ] ?? false,
      );
    } catch (error) {
      setHasPlayedIntro(false);
    }
  }, [gameData.details.slug]);

  if (hasPlayedIntro === undefined || showIntroVideo === undefined) return null;

  // Skip video based on LaunchDarkly flag (configured by device type)
  if (
    !gameData.gameContent?.video_splash_url ||
    showIntroVideo === false ||
    hasPlayedIntro
  ) {
    const bgUrl = gameData.details.background_url;

    return (
      <div className="flex w-full h-full">
        {bgUrl ? (
          <ImageBackground
            staticImageUrl={bgUrl}
            backdropClassName="bg-black/50"
          />
        ) : null}
        {children}
      </div>
    );
  }

  return <VideoIntroEnabled gameData={gameData}>{children}</VideoIntroEnabled>;
};
